/**
 * Validation utilities for parsing and validating form data
 */

/**
 * Safely parse a float from a string or return a default value
 * @param value - The string value to parse
 * @param defaultValue - The default value to return if parsing fails
 * @param min - Optional minimum value
 * @param max - Optional maximum value
 * @returns The parsed float or default value
 */
export const safeParseFloat = (
  value: string | null,
  defaultValue: number,
  min?: number,
  max?: number
): number => {
  if (value === null || value === '') {
    return defaultValue
  }

  const parsed = parseFloat(value)

  // Check if parsing resulted in NaN
  if (isNaN(parsed)) {
    return defaultValue
  }

  // Check if value is within min/max bounds
  if (min !== undefined && parsed < min) {
    return defaultValue
  }

  if (max !== undefined && parsed > max) {
    return defaultValue
  }

  return parsed
}

/**
 * Safely parse an integer from a string or return a default value
 * @param value - The string value to parse
 * @param defaultValue - The default value to return if parsing fails
 * @param min - Optional minimum value
 * @param max - Optional maximum value
 * @returns The parsed integer or default value
 */
export const safeParseInt = (
  value: string | null,
  defaultValue: number,
  min?: number,
  max?: number
): number => {
  if (value === null || value === '') {
    return defaultValue
  }

  const parsed = parseInt(value, 10)

  // Check if parsing resulted in NaN
  if (isNaN(parsed)) {
    return defaultValue
  }

  // Check if value is within min/max bounds
  if (min !== undefined && parsed < min) {
    return defaultValue
  }

  if (max !== undefined && parsed > max) {
    return defaultValue
  }

  return parsed
}

/**
 * Safely parse an optional float from a string
 * @param value - The string value to parse
 * @param min - Optional minimum value
 * @param max - Optional maximum value
 * @returns The parsed float or undefined
 */
export const safeParseOptionalFloat = (
  value: string | null,
  min?: number,
  max?: number
): number | undefined => {
  if (value === null || value === '') {
    return undefined
  }

  const parsed = parseFloat(value)

  // Return undefined if parsing resulted in NaN
  if (isNaN(parsed)) {
    return undefined
  }

  // Check if value is within min/max bounds
  if (min !== undefined && parsed < min) {
    return undefined
  }

  if (max !== undefined && parsed > max) {
    return undefined
  }

  return parsed
}

/**
 * Validate that a number is within a specified range
 * @param value - The number to validate
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns True if the value is within range
 */
export const isInRange = (value: number, min: number, max: number): boolean => {
  return value >= min && value <= max
}

/**
 * Validate ticker symbols
 * @param symbols - Array of ticker symbols
 * @returns True if all symbols are valid
 */
export const validateSymbols = (symbols: string[]): boolean => {
  if (symbols.length === 0) {
    return false
  }

  // Check each symbol
  return symbols.every(symbol => {
    // Must not be empty
    if (!symbol || symbol.trim() === '') {
      return false
    }

    // Should only contain alphanumeric characters, dots, and hyphens
    // Typical ticker format: AAPL, BRK.B, BRK-B, etc.
    const validTickerPattern = /^[A-Za-z0-9.-]+$/
    return validTickerPattern.test(symbol)
  })
}
